/**
 * Session signalling — deliberately dependency-free so it can be imported from anywhere in the
 * auth graph without creating a cycle.
 *
 * Two channels, both owned by lib/authSession.ts:
 * - cross-TAB "the session is dead" (CT-36 QA round 1): the access token is memory-only and
 *   per-tab, so a logout in one tab is invisible to the others until their token expires.
 *   BroadcastChannel carries the signal; a localStorage `storage` event is the fallback for
 *   contexts where BroadcastChannel is unavailable (older Safari, some embedded webviews).
 * - in-TAB "a fresh access token landed": lets caches keyed on token authority (/api/config,
 *   which carries the scopes) drop answers that were computed against a dead token.
 */

const SESSION_END_CHANNEL = "pbx-auth";
const SESSION_END_STORAGE_KEY = "pbx_session_end";

/** Why the session ended, when worth telling the user on the login page. Rides WITH the
 *  session-end signal so every receiving tab can show its own banner locally — a shared
 *  (persisted) toast is a consumable, and N reloading login pages racing to consume it is
 *  exactly the QA-3 "banner in one tab only" bug. */
export type SessionEndReason = "inactivity" | "revoked" | "password_expired" | "account_closed";

export function parseSessionEndReason(value: unknown): SessionEndReason | undefined {
    return value === "inactivity" || value === "revoked" || value === "password_expired" || value === "account_closed"
        ? value
        : undefined;
}

type SessionEndHandler = (reason?: SessionEndReason) => void;
type ActivityHandler = () => void;

let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === "undefined") {
        return null;
    }
    if (!broadcastChannel) {
        broadcastChannel = new BroadcastChannel(SESSION_END_CHANNEL);
    }
    return broadcastChannel;
}

/** Tell every OTHER tab on this origin that the session is over. Never delivered to this tab. */
export function postSessionEnd(reason?: SessionEndReason): void {
    try {
        getBroadcastChannel()?.postMessage({ type: "session-end", reason });
    } catch {
        // A closed/errored channel must never break the logout path.
    }

    try {
        // The value only has to CHANGE for the storage event to fire in the other tabs; the
        // key is removed immediately so nothing durable is left behind. performance.now() is
        // the change-maker (two logouts in one tab must both fire), the reason is the payload.
        localStorage.setItem(
            SESSION_END_STORAGE_KEY,
            JSON.stringify({ at: performance.now(), reason: reason ?? null })
        );
        localStorage.removeItem(SESSION_END_STORAGE_KEY);
    } catch {
        // Storage can be unavailable (private mode / blocked cookies) — BroadcastChannel above
        // is the primary path, this is only the fallback.
    }
}

/** Run `handler` when another tab reports the session ended. Returns an unsubscribe function. */
export function subscribeSessionEnd(handler: SessionEndHandler): () => void {
    const onBroadcast = (event: MessageEvent) => {
        if (event.data?.type === "session-end") {
            handler(parseSessionEndReason(event.data.reason));
        }
    };
    const onStorage = (event: StorageEvent) => {
        // newValue is null for the removeItem half of postSessionEnd — only react to the write.
        if (event.key === SESSION_END_STORAGE_KEY && event.newValue !== null) {
            let reason: SessionEndReason | undefined;
            try {
                reason = parseSessionEndReason(JSON.parse(event.newValue)?.reason);
            } catch {
                // Pre-reason format (a bare timestamp) from a tab still running old code.
            }
            handler(reason);
        }
    };

    getBroadcastChannel()?.addEventListener("message", onBroadcast);
    window.addEventListener("storage", onStorage);

    return () => {
        getBroadcastChannel()?.removeEventListener("message", onBroadcast);
        window.removeEventListener("storage", onStorage);
    };
}

/**
 * Cross-tab "the user is still here". The idle clock is per-tab, so without this a user
 * working in tab A would be logged out of tab B — and endSession broadcasts, so B would then drag
 * A out with it. Throttled by the caller; BroadcastChannel only (a missed activity ping just means
 * that tab leans on its own events, unlike a missed session-end which must never be lost).
 */
export function postActivity(): void {
    try {
        getBroadcastChannel()?.postMessage({ type: "activity" });
    } catch {
        // A closed/errored channel must never break the caller's own activity handling.
    }
}

export function subscribeActivity(handler: ActivityHandler): () => void {
    const onBroadcast = (event: MessageEvent) => {
        if (event.data?.type === "activity") {
            handler();
        }
    };

    getBroadcastChannel()?.addEventListener("message", onBroadcast);

    return () => {
        getBroadcastChannel()?.removeEventListener("message", onBroadcast);
    };
}

const sessionStartingHandlers = new Set<() => void>();

/** Fired by authSession just BEFORE the first token of a session lands (login, reset, callback, boot). */
export function emitSessionStarting(): void {
    for (const handler of sessionStartingHandlers) {
        try {
            handler();
        } catch {
            // One bad subscriber must not block the sign-in.
        }
    }
}

export function subscribeSessionStarting(handler: () => void): () => void {
    sessionStartingHandlers.add(handler);
    return () => {
        sessionStartingHandlers.delete(handler);
    };
}

const tokenEstablishedHandlers = new Set<() => void>();

/** Fired by authSession whenever a NEW access token is put into memory (login/refresh). */
export function emitTokenEstablished(): void {
    for (const handler of tokenEstablishedHandlers) {
        try {
            handler();
        } catch {
            // One bad subscriber must not stop the others (or the refresh that triggered it).
        }
    }
}

export function subscribeTokenEstablished(handler: () => void): () => void {
    tokenEstablishedHandlers.add(handler);
    return () => {
        tokenEstablishedHandlers.delete(handler);
    };
}

/** Test-only: drop subscribers and the cached channel between cases. */
export function resetSessionChannelForTests(): void {
    tokenEstablishedHandlers.clear();
    sessionStartingHandlers.clear();
    broadcastChannel?.close();
    broadcastChannel = null;
}
